// NeoCore · OKID · Verificaciones de identidad
// -----------------------------------------------------------------------------
// Tabla de todas las verificaciones OKID (tabla verificacion_identidad):
// decisión, % de match facial, RENAP, DPI vs RENAP, vigencia y PDF de evidencia.
// Al abrir una fila: detalle DOCUMENTO (OCR) vs DATA (RENAP) lado a lado + PDF.
//
// La foto de RENAP (imgs.PH_RENAP) queda pendiente: OKID confirmó un bug en su
// endpoint de imágenes. Mientras tanto la evidencia visual está en el PDF.
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Fingerprint, ArrowsClockwise, FilePdf, X } from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyObj = any

type Verif = {
  id: string
  asociado_id: string | null
  estado: string
  decision: 'pass' | 'review' | 'fail' | null
  match_pct: number | null
  seguridad: number | null
  status_okid: string | null
  renap_encontrado: boolean | null
  facial_match: boolean | null
  datos_coinciden: boolean | null
  vigencia_documento: string | null
  ph_renap: string | null
  fotos: { renap?: string; selfie?: string; dpi_front?: string; dpi_back?: string } | null
  okid_verification_id: string | null
  client_reference: string | null
  pdf_path: string | null
  documento: AnyObj
  data_renap: AnyObj
  resultado: AnyObj
  created_at: string
  asociado?: { nombre_completo?: string; no_asociado?: string } | null
}

const DEC = {
  pass: { label: 'APROBADO', fg: 'var(--ok-ink, #0f8a57)', bg: 'var(--ok-soft, #e6f5ee)' },
  review: { label: 'REVISAR', fg: 'var(--warning, #d97706)', bg: 'var(--warn-soft, #fef3e2)' },
  fail: { label: 'RECHAZADO', fg: 'var(--danger, #a32d2d)', bg: 'var(--danger-soft, #fdecec)' },
} as const

function fmtFecha(iso: string) {
  try {
    return new Date(iso).toLocaleString('es-GT', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'America/Guatemala' })
  } catch { return iso }
}

export function NeoCoreOkidVerificacionesPage() {
  const [rows, setRows] = useState<Verif[]>([])
  const [loading, setLoading] = useState(true)
  const [filtro, setFiltro] = useState<'todas' | 'pass' | 'review' | 'fail'>('todas')
  const [sel, setSel] = useState<Verif | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    const { data } = await supabase
      .from('verificacion_identidad')
      .select('*, asociado:asociado_id(nombre_completo, no_asociado)')
      .order('created_at', { ascending: false })
      .limit(200)
    setRows((data as Verif[]) ?? [])
    setLoading(false)
  }, [])
  useEffect(() => { void load() }, [load])

  const visibles = useMemo(
    () => (filtro === 'todas' ? rows : rows.filter((r) => r.decision === filtro)),
    [rows, filtro],
  )
  const conteos = useMemo(() => ({
    todas: rows.length,
    pass: rows.filter((r) => r.decision === 'pass').length,
    review: rows.filter((r) => r.decision === 'review').length,
    fail: rows.filter((r) => r.decision === 'fail').length,
  }), [rows])

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '4px 2px 40px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Fingerprint size={22} weight="duotone" />
          <h2 style={{ margin: 0, fontSize: 19, fontWeight: 800 }}>Verificaciones de identidad · OKID</h2>
        </div>
        <button className="btn btn-ghost" onClick={() => void load()} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <ArrowsClockwise size={15} /> Refrescar
        </button>
      </div>
      <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--muted-foreground)' }}>
        RENAP + OCR del DPI + comparación facial. La foto de RENAP se repinta en detalle cuando OKID habilite su endpoint de imágenes; mientras, la evidencia está en el PDF.
      </p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        {(['todas', 'pass', 'review', 'fail'] as const).map((f) => (
          <button key={f} onClick={() => setFiltro(f)} className={'btn ' + (filtro === f ? 'btn-primary' : 'btn-ghost')} style={{ fontSize: 12, padding: '5px 12px' }}>
            {f === 'todas' ? 'Todas' : DEC[f].label} · {conteos[f]}
          </button>
        ))}
      </div>

      <div style={{ background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: 14, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--hairline)', background: 'var(--bg)' }}>
              <th style={th}>Fecha</th>
              <th style={th}>Persona</th>
              <th style={{ ...th, textAlign: 'center' }}>Decisión</th>
              <th style={{ ...th, textAlign: 'right' }}>Match</th>
              <th style={{ ...th, textAlign: 'center' }}>RENAP</th>
              <th style={{ ...th, textAlign: 'center' }}>DPI vs RENAP</th>
              <th style={th}>Vigencia DPI</th>
              <th style={{ ...th, textAlign: 'center' }}>PDF</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td style={{ ...td, textAlign: 'center', color: 'var(--muted-foreground)' }} colSpan={8}>Cargando…</td></tr>
            ) : visibles.length === 0 ? (
              <tr><td style={{ ...td, textAlign: 'center', color: 'var(--muted-foreground)', padding: '28px 14px' }} colSpan={8}>
                No hay verificaciones todavía. Corré una desde <strong>OKID · test</strong> o desde la ficha de un asociado.
              </td></tr>
            ) : visibles.map((r) => {
              const nombre = r.asociado?.nombre_completo
                || [r.documento?.PRIMER_NOMBRE, r.documento?.SEGUNDO_NOMBRE, r.documento?.PRIMER_APELLIDO, r.documento?.SEGUNDO_APELLIDO].filter(Boolean).join(' ')
                || '—'
              return (
                <tr key={r.id} onClick={() => setSel(r)} style={{ borderBottom: '1px solid var(--line-soft)', cursor: 'pointer' }}>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtFecha(r.created_at)}</td>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{nombre}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>
                      {r.asociado?.no_asociado ? `#${r.asociado.no_asociado}` : (r.asociado_id ? '' : 'prueba')}
                    </div>
                  </td>
                  <td style={{ ...td, textAlign: 'center' }}><DecisionPill decision={r.decision} /></td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 800, fontFeatureSettings: '"tnum"' }}>
                    {r.match_pct != null ? `${r.match_pct}%` : '—'}
                  </td>
                  <td style={{ ...td, textAlign: 'center' }}>{tick(r.renap_encontrado)}</td>
                  <td style={{ ...td, textAlign: 'center' }}>{tick(r.datos_coinciden)}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{r.vigencia_documento ?? '—'}</td>
                  <td style={{ ...td, textAlign: 'center' }}>
                    {r.pdf_path ? <FilePdf size={18} color="var(--brand)" /> : <span style={{ color: 'var(--muted-foreground)' }}>—</span>}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {sel && <DetalleModal v={sel} onClose={() => setSel(null)} />}
    </div>
  )
}

function DetalleModal({ v, onClose }: { v: Verif; onClose: () => void }) {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [fotoUrls, setFotoUrls] = useState<{ renap?: string; selfie?: string; dpi_front?: string; dpi_back?: string }>({})
  useEffect(() => {
    if (v.pdf_path) {
      supabase.storage.from('okid').createSignedUrl(v.pdf_path, 3600).then(({ data }) => {
        if (data?.signedUrl) setPdfUrl(data.signedUrl)
      })
    }
    const f = v.fotos ?? {}
    const entries = Object.entries(f).filter(([, p]) => !!p) as [string, string][]
    if (entries.length) {
      supabase.storage.from('okid').createSignedUrls(entries.map(([, p]) => p), 3600).then(({ data }) => {
        if (!data) return
        const map: Record<string, string> = {}
        entries.forEach(([k], i) => { const u = data[i]?.signedUrl; if (u) map[k] = u })
        setFotoUrls(map)
      })
    }
  }, [v.pdf_path, v.fotos])

  const doc = v.documento ?? {}
  const dat = v.data_renap ?? {}
  const campos: Array<[string, string, string]> = [
    ['Primer nombre', doc.PRIMER_NOMBRE, dat.PRIMER_NOMBRE],
    ['Segundo nombre', doc.SEGUNDO_NOMBRE, dat.SEGUNDO_NOMBRE],
    ['Tercer nombre', doc.TERCER_NOMBRE, dat.TERCER_NOMBRE],
    ['Primer apellido', doc.PRIMER_APELLIDO, dat.PRIMER_APELLIDO],
    ['Segundo apellido', doc.SEGUNDO_APELLIDO, dat.SEGUNDO_APELLIDO],
    ['CUI', doc.CUI, dat.CUI],
    ['Fecha nacimiento', doc.FECHA_NACIMIENTO, dat.FECHA_NACIMIENTO],
    ['Género', doc.GENERO, dat.GENERO],
  ]

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--surface)', borderRadius: 14, width: 720, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', padding: 22, border: '1px solid var(--hairline)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <DecisionPill decision={v.decision} big />
            {v.match_pct != null && <span style={{ fontSize: 22, fontWeight: 800 }}>{v.match_pct}%</span>}
          </div>
          <button onClick={onClose} style={{ all: 'unset', cursor: 'pointer', color: 'var(--muted-foreground)' }}><X size={18} /></button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
          <Mini k="RENAP" v={v.status_okid ?? (v.renap_encontrado ? 'resuelto' : '—')} />
          <Mini k="Rostro (SEGURIDAD)" v={v.facial_match ? '✓ coincide' : '✗'} />
          <Mini k="DPI vs RENAP" v={v.datos_coinciden ? '✓ coinciden' : '✗ discrepan'} />
          <Mini k="Vigencia DPI" v={v.vigencia_documento ?? '—'} />
          <Mini k="VERIFICATION_ID" v={v.okid_verification_id ?? '—'} />
          <Mini k="Referencia" v={v.client_reference ?? '—'} />
        </div>

        {/* Fotos devueltas por OKID: RENAP (fuente de verdad) vs selfie enviado */}
        {(fotoUrls.renap || fotoUrls.selfie || fotoUrls.dpi_front || fotoUrls.dpi_back) ? (
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            <Foto url={fotoUrls.renap} label="RENAP · en línea" destacado />
            <Foto url={fotoUrls.selfie} label="Selfie enviado" />
            <Foto url={fotoUrls.dpi_front} label="DPI frente" />
            <Foto url={fotoUrls.dpi_back} label="DPI dorso" />
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '12px 14px', borderRadius: 10, background: 'var(--bg-subtle, #f6f6f7)', border: '1px solid var(--hairline)', marginBottom: 16, fontSize: 12, color: 'var(--muted-foreground)' }}>
            <Fingerprint size={20} />
            <span>Sin fotos guardadas para esta verificación (verificación previa al fix de OKID). La evidencia está en el PDF.</span>
          </div>
        )}

        <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
          Documento (OCR) vs RENAP
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 16 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--hairline)' }}>
              <th style={{ ...th, padding: '6px 10px' }}>Campo</th>
              <th style={{ ...th, padding: '6px 10px' }}>DPI (OCR)</th>
              <th style={{ ...th, padding: '6px 10px' }}>RENAP</th>
            </tr>
          </thead>
          <tbody>
            {campos.filter(([, a, b]) => a || b).map(([label, a, b]) => {
              const match = norm(a) === norm(b)
              return (
                <tr key={label} style={{ borderBottom: '1px solid var(--line-soft)' }}>
                  <td style={{ padding: '7px 10px', color: 'var(--muted-foreground)', fontWeight: 600 }}>{label}</td>
                  <td style={{ padding: '7px 10px' }}>{a ?? '—'}</td>
                  <td style={{ padding: '7px 10px', color: match ? 'var(--ink)' : 'var(--danger, #a32d2d)', fontWeight: match ? 400 : 700 }}>{b ?? '—'}</td>
                </tr>
              )
            })}
          </tbody>
        </table>

        {/* Datos extra de RENAP (útiles para el FEIS) */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
          <Mini k="Estado civil" v={dat.ESTADO_CIVIL ?? '—'} />
          <Mini k="Ocupación" v={dat.OCUPACION ?? '—'} />
          <Mini k="Nacionalidad" v={dat.NACIONALIDAD ?? '—'} />
          <Mini k="Vecindad" v={dat.VECINDAD ?? '—'} />
          <Mini k="Depto nacimiento" v={dat.DEPTO_NACIMIENTO ?? '—'} />
          <Mini k="Muni nacimiento" v={dat.MUNI_NACIMIENTO ?? '—'} />
        </div>

        {pdfUrl && (
          <a href={pdfUrl} target="_blank" rel="noreferrer" className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7, width: '100%' }}>
            <FilePdf size={17} /> Ver PDF de evidencia →
          </a>
        )}
      </div>
    </div>
  )
}

function norm(s?: string | null): string {
  return (s ?? '').toUpperCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/\s+/g, ' ').trim()
}

function tick(v: boolean | null) {
  if (v == null) return <span style={{ color: 'var(--muted-foreground)' }}>—</span>
  return v
    ? <span style={{ color: 'var(--ok-ink, #0f8a57)', fontWeight: 800 }}>✓</span>
    : <span style={{ color: 'var(--danger, #a32d2d)', fontWeight: 800 }}>✗</span>
}

function DecisionPill({ decision, big }: { decision: Verif['decision']; big?: boolean }) {
  if (!decision) return <span style={{ color: 'var(--muted-foreground)' }}>—</span>
  const d = DEC[decision]
  return (
    <span style={{ display: 'inline-block', fontSize: big ? 13 : 10, fontWeight: 800, padding: big ? '5px 14px' : '3px 10px', borderRadius: 99, background: d.bg, color: d.fg, letterSpacing: 0.4 }}>
      {d.label}
    </span>
  )
}

function Foto({ url, label, destacado }: { url?: string; label: string; destacado?: boolean }) {
  if (!url) return null
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, alignItems: 'center' }}>
      <img
        src={url}
        alt={label}
        style={{
          width: destacado ? 132 : 104, height: destacado ? 132 : 104, objectFit: 'cover',
          borderRadius: 12, border: destacado ? '2px solid var(--ok-ink, #0f8a57)' : '1px solid var(--hairline)',
        }}
      />
      <span style={{ fontSize: 11, fontWeight: 700, color: destacado ? 'var(--ok-ink, #0f8a57)' : 'var(--muted-foreground)' }}>{label}</span>
    </div>
  )
}

function Mini({ k, v }: { k: string; v: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 0.6 }}>{k}</span>
      <span style={{ fontSize: 13, fontWeight: 600 }}>{v}</span>
    </div>
  )
}

const th: React.CSSProperties = {
  textAlign: 'left', padding: '10px 14px',
  fontSize: 10, fontWeight: 800, color: 'var(--muted-foreground)',
  textTransform: 'uppercase', letterSpacing: 1.2,
}
const td: React.CSSProperties = { padding: '12px 14px' }
